import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import moment from "moment-timezone";
import { Dropdown, MenuItem } from 'react-bootstrap';
import { Menu } from 'react-bootstrap/lib/Dropdown';
import DropdownToggle from 'react-bootstrap/lib/DropdownToggle';
import ScrollBar from "ScrollBar";
import ConsoleStore from 'ConsoleStore';
import TextToSpeech from './TextToSpeech';
import GeneratePdf from './GeneratePdf';
import AiInsightsContentMarkdown from './AiInsightsContentMarkdown';
import AiInsightsContentSkeleton from './AiInsightsContentSkeleton';
import AiInsightsRefresh from './AiInsightsRefresh';
import AiInsightsTextSize from './AiInsightsTextSize';
import { fetchAiInsightsSelectedReportData, setAiInsightsFeedback } from '../../../../Actions/AiInsightsActions';
import Pedigree from './CeoPedigree';

const AiInsightsContent = ({ selectedCategory, selectedSubCategoryId, message, aiInsightsSelectedReportData, aiInsightsCategories, setAiInsightsFeedback, aiInsightsSelectedReportTitle, isInsightReportFetched, lastGeneratedTime, fetchAiInsightsSelectedReportData, isMiniListOpen, SymbolInfo }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [deltaTextSize, setDeltaTextSize] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);
  const scrollContainerRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const [intervalId, setIntervalId] = useState(null);
  const scrollBarRef = useRef(null);

  const categoryObj = aiInsightsCategories?.find((cat) => cat[selectedCategory]);
  const subCategories = categoryObj ? categoryObj[selectedCategory] : [];
  const date = moment().format("ddd DD MMM YYYY");
  const contentRef = React.useRef(null);
  const selectedSubCategory = subCategories.find((sub) => sub.id === selectedSubCategoryId);
  const trackUsageProps = ["AI", selectedSubCategory?.label, "AI Insights", SymbolInfo.Symbol]
  const isCeoPedigree = selectedSubCategoryId === 11;

  // Auto-scroll to bottom when new content is added during request
  useEffect(() => {
    if (!isInsightReportFetched && aiInsightsSelectedReportData) {
      const el = scrollContainerRef.current;
      if (el && !userScrolled) {
        setIsAutoScrolling(true);
        el.scrollTop = el.scrollHeight;
        
        // Clear any existing timeout
        if (autoScrollTimeoutRef.current) {
          clearTimeout(autoScrollTimeoutRef.current);
        }
        
        // Set a small delay to ensure the scroll happens after content update
        autoScrollTimeoutRef.current = setTimeout(() => {
          setIsAutoScrolling(false);
        }, 100);
      }
    }
  }, [aiInsightsSelectedReportData, isInsightReportFetched, userScrolled]);

  // Reset scroll behavior when request starts
  useEffect(() => {
    if (!isInsightReportFetched) {
      setUserScrolled(false);
      setIsAutoScrolling(false);
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
    }
  }, [isInsightReportFetched]);

  // Scroll to top when new report is fetched
  useEffect(() => {
    if (isInsightReportFetched) {
      const el = scrollContainerRef.current;
      if (el) {
        setUserScrolled(false);
        setIsAutoScrolling(false);
      }
    }
  }, [isInsightReportFetched]);

  // Handle manual scroll events
  const handleScroll = (e) => {
    if (!isInsightReportFetched && !isAutoScrolling) {
      const el = e.target;
      const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;
      
      if (!isAtBottom) {
        setUserScrolled(true);
      } else {
        setUserScrolled(false);
      }
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isInsightReportFetched) {
      const interval = setInterval(() => {
        if (scrollBarRef.current) {
          scrollBarRef.current.refresh();
        }
      }, 2000);
      setIntervalId(interval);
    } else {
      clearInterval(intervalId)
    }
  }, [isInsightReportFetched])

  const handleReaction = (text, reaction, comments) => {
    ConsoleStore.trackUsage("AIFeedbackAdded", "", trackUsageProps);
    setAiInsightsFeedback(text, reaction, comments);
  };


  const increaseSize = () => {
    ConsoleStore.trackUsage("FontSizeUpdated", "increase", trackUsageProps);
    setDeltaTextSize((previous) => {
      if (previous < 12) {
        return previous + 1;
      }
      return previous;
    });
  }


  const reduceSize = () => {
    ConsoleStore.trackUsage("FontSizeUpdate", "decrease", trackUsageProps);
    setDeltaTextSize((previous) => {
      if (previous > -7) {
        return previous - 1;
      }
      return previous;
    });
  }


  const aiInsightsContentHeaderDropdown = () => (
    <div className='section'>
      <span className='section-name'>{selectedCategory}{selectedCategory && <>:&nbsp;</>}</span>
      {
        subCategories?.length > 1 ?
          <Dropdown
            id={`subcategory-dropdown-${selectedCategory}`}
            onToggle={(open) => setIsOpen(open)}
            bsStyle="link"
            open={isOpen}
            // className='custom-scroll-light'
          >
            <DropdownToggle
              noCaret
              bsStyle="default"
              className="subcategory-dropdown-label"
            >
              <span className="custom-dropdown-title">
                {selectedSubCategory?.label}
                {/* svg for custom caret */}
                <svg
                  className={`outlined-caret ${isOpen ? 'rotated' : ''}`}
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="5,12 12.5,20 20,12" fill="none" />
                </svg>
              </span>
            </DropdownToggle>
            <Menu
              // style={{
              //   height: subCategories?.length < 4 ? subCategories?.length * 27 + "px" : "81px"
              // }}
            >
              <div 
              // id="AiInsightsContentDropdownScroll"
              >
                {subCategories.map((sub) => (
                  <MenuItem
                    key={sub.id}
                    eventKey={sub.id}
                    active={selectedSubCategory?.id === sub.id}
                    onClick={() => {
                      if (!sub.isDisabled) {
                        fetchAiInsightsSelectedReportData(selectedCategory, sub.id);
                        ConsoleStore.trackUsage("MenuItemClicked", "", ["AI", sub.label, "AI Insights", SymbolInfo.Symbol]);
                        setIsOpen(false);
                      }
                    }}
                    className={sub.isDisabled ? 'disabled' : ''}
                  >
                    <u className="icn-select" style={{ visibility: selectedSubCategory?.id === sub.id ? 'visible' : 'hidden' }}></u>
                    <span>{sub.label}</span>
                  </MenuItem>
                ))}
                {/* <ScrollBar scrollId="AiInsightsContentDropdownScroll" vScroll={true} scrollOnHover={true} /> */}
              </div>
            </Menu>
          </Dropdown>
          : selectedSubCategory && (
            <span className='report-label'>{selectedSubCategory.label}</span>
          )}
    </div>
  );


  const aiInsightsRenderTools = () => (
    <div className='section'>
      {
        lastGeneratedTime && <AiInsightsRefresh isDisable={!isInsightReportFetched} lastGeneratedTime={lastGeneratedTime} handleClick={() => {
          fetchAiInsightsSelectedReportData(selectedCategory, selectedSubCategoryId);
          ConsoleStore.trackUsage("ReportRefreshed", "", trackUsageProps);
        }} />
      }
      <AiInsightsTextSize isDisable={!isInsightReportFetched || isCeoPedigree} increaseSize={increaseSize} reduceSize={reduceSize} />
      <TextToSpeech isDisable={!isInsightReportFetched || isCeoPedigree} message={aiInsightsSelectedReportData} trackUsageProps={trackUsageProps} />
      <GeneratePdf isDisable={!isInsightReportFetched} contentRef={contentRef} trackUsageProps={trackUsageProps} />
    </div>
  );


  const aiInsightsContentHeader = () => (
    <div className="ai-insights-content-header">
      {aiInsightsContentHeaderDropdown()}
      {aiInsightsRenderTools()}
    </div>
  );


  const aiInsightsReportTitle = () => (
    <div className='ai-insights-report-title'>
      {aiInsightsSelectedReportTitle}
    </div>
  );


  const aiInsightsContentReport = () => (
    <>
      <div className='ai-insights-report-container custom-scroll'>
        <div id="aiInsightsCustomScroll" className='ai-insights-report' ref={scrollContainerRef} onScroll={handleScroll}>
          <div style={{ height: "100%" }} ref={contentRef}>
            <div className='ai-insights-report-print'>
              <div className='ai-insights-report-print-text'>
                {aiInsightsSelectedReportTitle}
              </div>
              <div className='ai-insights-report-print-date'>
                {date}
              </div>
            </div>
            {message ?
              message === "Loading ..." ? (
                <AiInsightsContentSkeleton />
              ) : <div className='ai-insights-unavailable'>{message}</div>
              : (
                  selectedSubCategoryId === 11 ? 
                    <Pedigree 
                      report={aiInsightsSelectedReportData} 
                      handleReaction={handleReaction} 
                      isMiniListOpen={isMiniListOpen}
                    /> :
                    <AiInsightsContentMarkdown 
                      data={aiInsightsSelectedReportData} 
                      deltaTextSize={deltaTextSize} 
                      handleReaction={handleReaction} 
                      isMiniListOpen={isMiniListOpen}
                    ></AiInsightsContentMarkdown>
              )}
          </div>
        </div>
      </div>

      <ScrollBar ref={scrollBarRef} scrollId="aiInsightsCustomScroll" vScroll={true} shouldUpdate={isInsightReportFetched} />
    </>
  );


  return (
    <div className="ai-insights-content">
      {aiInsightsContentHeader()}
      {aiInsightsReportTitle()}
      {aiInsightsContentReport()}
    </div>
  );
};


const mapStateToProps = ({ AiInsightsReducer, DatagraphReducers }) => {
  const { message, aiInsightsSelectedReportData, aiInsightsCategories, aiInsightsSelectedReportTitle, aiInsightsSettings, isInsightReportFetched, lastGeneratedTime } = AiInsightsReducer;
  const { selectedCategory, selectedSubCategoryId } = aiInsightsSettings;
  const { isMiniListOpen, SymbolInfo } = DatagraphReducers.DataGraphReducer;

  return { message, aiInsightsSelectedReportData, selectedCategory, selectedSubCategoryId, aiInsightsCategories, aiInsightsSelectedReportTitle, isInsightReportFetched, lastGeneratedTime, isMiniListOpen, SymbolInfo };
};


const mapDispatchToProps = (dispatch) => ({
  setAiInsightsFeedback: (text, reaction, comments) =>
    dispatch(setAiInsightsFeedback(text, reaction, comments)),
  fetchAiInsightsSelectedReportData: (selectedCategory, selectedSubCategoryId) => dispatch(fetchAiInsightsSelectedReportData(selectedCategory, selectedSubCategoryId))
});


export default connect(mapStateToProps, mapDispatchToProps)(AiInsightsContent);
